import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef, 
         Output, EventEmitter, HostListener, Inject, Renderer2 } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';

import {
    ResolutionCenterService,
    MessageRequest,
    ReplyMessageRequest,
    AVAILABLE_REACTIONS,
    ChatParticipant,
    ChatRoom,
    ChatUser,
    ClientChat,
    ClientMessage,
    ChatRoomMessage,
    Attachment,
    ReactionsBreakdown,
    BroadcastAudience,
    BroadcastMessage,
    BroadcastUpsertRequest,
} from '@app/resolution-center/resolution-center.service';
import {
    getObjectAttachmentIcon as getObjectAttachmentIconPath,
    getObjectAttachmentImageUrl as getObjectAttachmentImageUrlValue,
    getObjectAttachmentLabel as getChatObjectAttachmentLabel,
    getObjectAttachmentMeta as getObjectAttachmentMetaValue,
    getObjectAttachmentName as getObjectAttachmentNameValue,
    isFileAttachment as isChatFileAttachment,
    isObjectAttachment as isObjectTypeAttachment,
} from '@app/resolution-center/chat-attachment.util';

import { NotificationsService } from '@shared/notifications.service';
import { InteractionService } from '@app/interaction/interaction.service';
import { ConfirmStatus } from '@app/interaction/confirm/confirm.component';
import { GalleryItem } from '@app/gallery/gallery-item';
import { UserTag } from '@app/user/user.service';
import { environment } from '@environments/environment';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { ScheduleMessageModalComponent } from './schedule-message-modal/schedule-message-modal.component';
import { ScheduledMessagesModalComponent } from './scheduled-messages-modal/scheduled-messages-modal.component';

@Component({
    selector: 'app-messages-tab',
    templateUrl: './messages-tab.component.html',
    styleUrls: ['./messages-tab.component.scss'],
    standalone: false
})

export class MessagesTabComponent implements OnInit, OnChanges, OnDestroy {
    @Input() context: 'profile' | 'chat' | 'chatRoom' | 'broadcast'; // Explicit context to determine behavior even when no item is selected
    @Input() user?: ChatUser;
    @Input() chat?: ClientChat;
    @Input() chatRoom?: ChatRoom;
    /** When context is broadcast, the selected audience (scope is read from here for PUT /admin/broadcast/{id}). */
    @Input() broadcastAudience?: BroadcastAudience | null;
    /** When set, the next send updates this broadcast (PUT uses this id). */
    @Input() broadcastEditingMessage?: BroadcastMessage | null;
    /** Placeholder for the message textarea when context is broadcast. */
    @Input() broadcastMessagePlaceholder?: string;
    @Input() userTags?: UserTag[];
    @Input() resetTrigger?: number; // Trigger to force reset when chat is already null
    /** When true, favorited/archived toggles only emit; parent will refresh list and clear selection. Avoids star disappearing before panel closes. */
    @Input() refreshListOnFlagsChange = false;
    @Input() isCoach: boolean = false;
    @Output() messageSent = new EventEmitter<boolean>();
    @Output() chatUpdated = new EventEmitter<ClientChat>();
    @Output() chatRoomUpdated = new EventEmitter<ChatRoom>();
    @Output() chatCreated = new EventEmitter<boolean>(); // Emit true when new chat is created to trigger reload
    @Output() createNewChatRoom = new EventEmitter<void>(); // Emit when user wants to create a new chat room
    @Output() editCustomChatRoom = new EventEmitter<ChatRoom>(); // Emit when user wants to edit a custom chat room
    @Output() editSystemChatRoom = new EventEmitter<ChatRoom>(); // Emit when user wants to edit a system chat room
    @Output() addMembersToChatRoom = new EventEmitter<ChatRoom>(); // Emit when user wants to add members via chat room modal
    @Output() broadcastMessageUpserted = new EventEmitter<BroadcastMessage>();
    @Output() broadcastMessageRemoved = new EventEmitter<number>();
    @Output() broadcastEditCancelled = new EventEmitter<void>();

    // Source type determines which input is active and how messages are loaded/sent/reactions etc
    get source(): 'profile' | 'chat' | 'chatRoom' | 'broadcast' | null {
        if (this.context) {
            if (this.context === 'broadcast' && this.broadcastAudience?.audience_key) return 'broadcast';
            if (this.chatRoom?.id && this.context === 'chatRoom') return 'chatRoom';
            // For 'chat' context: return 'chat' if we have a chat OR if we have a user (new chat scenario)
            if (this.context === 'chat' && (this.chat?.id || this.user?.id)) return 'chat';
            if (this.user?.id && this.context === 'profile') return 'profile';
        }

        return null;
    }
    
    // Get the context type for empty state display
    get contextType(): 'profile' | 'chat' | 'chatRoom' | 'broadcast' | null {
        return this.context || this.source;
    }

    isEditingBroadcastMessage(): boolean {
        return this.context === 'broadcast' && !!this.broadcastEditingMessage;
    }

    isComposerEditMode(): boolean {
        return this.isEditingScheduledMessage() || this.isEditingBroadcastMessage();
    }

    cancelComposerEdit(): void {
        if (this.isEditingScheduledMessage()) {
            this.scheduledMessageBeingEdited = null;
            this.messageText = '';
            return;
        }
        if (this.isEditingBroadcastMessage()) {
            this.broadcastEditCancelled.emit();
            this.messageText = '';
            this.resetAttachmentState();
            this.scheduledDateTime = null;
        }
    }

    isComposerSubmitDisabled(): boolean {
        if (this.isEditingScheduledMessage()) {
            return !this.messageText?.trim();
        }
        if (this.isEditingBroadcastMessage()) {
            return (
                (!this.messageText?.trim() && (!this.uploadedFiles || this.uploadedFiles.length === 0)) ||
                this.sending ||
                this.uploadingFiles ||
                !this.source
            );
        }
        return this.isMessageSendDisabled();
    }

    messages: ClientMessage[] = [];
    messageText: string = '';
    loadingMessages: boolean = false;
    showLoadMoreMessages: boolean = false;
    
    private messagesPage: number = 1;
    private previousUserId: number | null = null;
    private previousChatId: number | null = null; // Track previous chat ID to detect chat changes
    private previousChatRoomId: number | null = null; // Track previous chat room ID to detect chat room changes
    private internalUser: ChatUser | undefined = undefined; // Track user set internally vs from input
    private hasAttemptedLoad: boolean = false; // Track if we've attempted to load messages to prevent infinite loops

    /** Polling for new messages (no WebSockets). Interval id so we can clear on destroy or context change. */
    private newMessagesPollIntervalId: ReturnType<typeof setInterval> | null = null;
    private readonly NEW_MESSAGES_POLL_INTERVAL_MS = 30000; // 30 seconds

    sending: boolean = false;
    replyingToMessage: ClientMessage | null = null;
    replyingToAttachment: Attachment | null = null;

    uploadedFiles: GalleryItem[] = [];
    uploadingFiles: boolean = false;
    uploadProgress: number = 0;
    currentUploadIndex: number = 0;
    totalUploads: number = 0;
    
    private fileList: File[] = [];
    private _uploadedCounter: number = 0;
    private currentAttachmentType: 'image' | 'video' | 'document' | null = null;
    readonly MAX_ATTACHMENTS_PER_MESSAGE = 6;

    showEmojiPicker: boolean = false;
    showGifPicker: boolean = false;
    gifs: any[] = [];
    loadingGifs: boolean = false;
    gifSearchQuery: string = '';
    private gifSearchSubject = new Subject<string>();
    gifOffset: number = 0;
    hasMoreGifs: boolean = true;

    showReactionPicker: number | null = null;
    showAttachmentReactionPicker: number | null = null;
    availableReactions: string[] = AVAILABLE_REACTIONS;
    showGroupChatMenu: boolean = false;

    // Reactions breakdown modal state
    showReactionsBreakdown = false;
    reactionsBreakdownLoading = false;
    reactionsBreakdown: ReactionsBreakdown | null = null;

    // Image modal state (used when viewing message attachments)
    showImageModal = false;
    isClosingImageModal = false;

    // Manage members modal (chat room members)
    showManageMembersModal = false;
    selectedImage: any = null;

    // Scheduled messages (for chats and chat rooms)
    scheduledMessages: (ClientMessage | ChatRoomMessage | BroadcastMessage)[] = [];
    private scheduledMessageToReschedule: ClientMessage | ChatRoomMessage | BroadcastMessage | null = null;
    scheduledMessageBeingEdited: ClientMessage | ChatRoomMessage | BroadcastMessage | null = null;

    // User picker state (for new chat creation)
    selectedUserForNewChat: ChatUser | null = null;
    userSearchResults: ChatUser[] = [];
    userSearchQuery: string = '';
    searchingUsers: boolean = false;
    private userSearchSubject = new Subject<string>();
    private isNewChat: boolean = false; // Track if this is a new chat (no chat yet)
    wasInputFocused: boolean = false; // Track if input was focused before search

    @ViewChild('messagesList', { static: false }) messagesList: ElementRef;
    @ViewChild('fileInput', { static: false }) fileInput: ElementRef<HTMLInputElement>;
    @ViewChild('videoInput', { static: false }) videoInput: ElementRef<HTMLInputElement>;
    @ViewChild('documentInput', { static: false }) documentInput: ElementRef<HTMLInputElement>;
    @ViewChild('emojiPickerButton', { static: false }) emojiPickerButton: ElementRef;
    @ViewChild('gifPickerButton', { static: false }) gifPickerButton: ElementRef;
    @ViewChild('gifPickerContainer', { static: false }) gifPickerContainer: ElementRef;
    @ViewChild('userSearchInput', { static: false }) userSearchInput: ElementRef<HTMLInputElement>;
    @ViewChild('messageInputRef', { static: false }) messageInputRef: ElementRef<HTMLTextAreaElement>;

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        
        if (this.showEmojiPicker && this.emojiPickerButton) {
            const pickerContainer = target.closest('.emoji-picker-container');
            const emojiButton = target.closest('.btn-icon');
            
            if (!pickerContainer && !emojiButton) {
                this.showEmojiPicker = false;
            }
        }
        
        if (this.showGifPicker && this.gifPickerButton) {
            const pickerContainer = target.closest('.gif-picker-container');
            const gifButton = target.closest('.btn-icon');
            
            if (!pickerContainer && !gifButton) {
                this.showGifPicker = false;
            }
        }
        
        if (this.showReactionPicker !== null) {
            const pickerContainer = target.closest('.reaction-picker-popup');
            const reactionButton = target.closest('.btn-icon-sm');
            
            if (!pickerContainer && !reactionButton) {
                this.showReactionPicker = null;
            }
        }

        if (this.showAttachmentReactionPicker !== null) {
            const pickerContainer = target.closest('.attachment-reaction-picker-popup');
            const reactionButton = target.closest('.attachment-reaction-btn');

            if (!pickerContainer && !reactionButton) {
                this.showAttachmentReactionPicker = null;
            }
        }

        if (this.showGroupChatMenu) {
            const menuContainer = target.closest('.group-chat-menu-popup');
            const menuButton = target.closest('.header-action-btn');

            if (!menuContainer && !menuButton) {
                this.showGroupChatMenu = false;
            }
        }
    }

    private bsModalRef: BsModalRef;
    scheduledDateTime: Date | null = null;

    constructor(
        private resolutionCenterService: ResolutionCenterService,
        private notifications: NotificationsService,
        private interaction: InteractionService,
        private http: HttpClient,
        private renderer: Renderer2,
        private modalService: BsModalService,
        @Inject(DOCUMENT) private document: Document,
        private sanitizer: DomSanitizer
    ) {
        // Setup GIF search debouncing
        this.gifSearchSubject.pipe(
            debounceTime(500),
            distinctUntilChanged()
        ).subscribe(query => {
            query.trim() ? this.searchGifs(query) : this.loadTrendingGifs();
        });

        // Setup user search debouncing
        this.userSearchSubject.pipe(
            debounceTime(400),
            distinctUntilChanged()
        ).subscribe(query => {
            this.searchUsers(query);
        });
    }

    ngOnInit(): void {
        //setting the height of the content to 100vh
        const contentElement = this.document.querySelector('.content');
        if (contentElement) {
            this.renderer.setStyle(contentElement, 'height', '100vh');
        }

        // Initialize tracking variables
        this.previousUserId = this.user?.id ?? null;
        this.previousChatId = this.chat?.id ?? null;
        this.previousChatRoomId = this.chatRoom?.id ?? null;

        // Check if we're in new chat mode (no source)
        const currentSource = this.source;
        this.isNewChat = !this.chat; // Update: if there's no chat, it's a new chat

        // Load messages if we have a source
        // For profile context, we need a chat to load messages, but we can still send messages without a chat
        if (currentSource) {
            this.hasAttemptedLoad = false; // Reset before initial load
            // Only load messages if we have a chat (for profile/chat context) or chatRoom
            if ((currentSource === 'profile' && this.chat?.id) || 
                (currentSource === 'chat' && this.chat?.id) || 
                (currentSource === 'chatRoom' && this.chatRoom?.id)) {
                this.loadMessages();
            }
            if (currentSource === 'broadcast' && this.broadcastAudience?.audience_key) {
                void this.loadScheduledMessages();
            }
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Check if reset trigger changed (parent clicked "New" button)
        // This MUST be checked first and return early to prevent other logic from running
        if (changes['resetTrigger'] && this.resetTrigger && this.resetTrigger > 0) {
            // Always reset when trigger changes, regardless of current state
            // This ensures picker mode is shown when "New" is clicked
            this.resetComponentState();
            // Force return - don't process any other changes
            return;
        }

        if (this.context === 'broadcast') {
            if (changes['broadcastAudience']) {
                const prev = changes['broadcastAudience'].previousValue as BroadcastAudience | null | undefined;
                const cur = this.broadcastAudience;
                const keyChanged = (prev?.audience_key ?? null) !== (cur?.audience_key ?? null);
                if (!changes['broadcastAudience'].firstChange && keyChanged) {
                    this.messageText = '';
                    this.resetAttachmentState();
                    this.scheduledDateTime = null;
                    this.scheduledMessageBeingEdited = null;
                    this.cancelReply();
                }
                if (cur?.audience_key) {
                    void this.loadScheduledMessages();
                } else {
                    this.scheduledMessages = [];
                }
            }
            if (changes['broadcastEditingMessage']) {
                if (this.broadcastEditingMessage) {
                    this.messageText = this.broadcastEditingMessage.message ?? '';
                    setTimeout(() => this.adjustMessageInputHeight(), 0);
                } else if (!changes['broadcastEditingMessage'].firstChange) {
                    this.messageText = '';
                    this.resetAttachmentState();
                    this.scheduledDateTime = null;
                }
            }
            return;
        }
        
        // Check if chatRoom changed
        if (changes['chatRoom']) {
            const chatRoomIdChanged = this.chatRoom?.id !== this.previousChatRoomId;
            
            if (chatRoomIdChanged) {
                // Chat room changed - reset and load messages
                this.stopNewMessagesPoll();
                this.messages = [];
                this.messageText = '';
                this.messagesPage = 1;
                this.showLoadMoreMessages = false;
                this.replyingToMessage = null;
                this.replyingToAttachment = null;
                this.resetAttachmentState();
                this.hasAttemptedLoad = false; // Reset load attempt flag when chat room changes
                this.previousChatRoomId = this.chatRoom?.id ?? null;
                this.isNewChat = false;
                
                if (this.chatRoom?.id) {
                    this.loadMessages();
                }
            }
            
            // If chatRoom is set, it takes priority - don't process profile/chat changes
            if (this.chatRoom?.id) {
                return;
            }
        }
        
        // If we're in reset state (no user, no chat no chatRoom), don't process other changes
        if (!this.user && !this.chat && !this.chatRoom && !this.internalUser) {
            this.isNewChat = true;
            return;
        }
        
        // Track if input user was explicitly cleared by parent
        const inputUserCleared = changes['user'] && !this.user && changes['user'].previousValue;
        const inputChatCleared = changes['chat'] && !this.chat && changes['chat'].previousValue;
        
        // If all inputs are undefined/null, we should be in picker mode
        const inputUserIsUndefined = !this.user; // Current input value from parent
        const inputChatIsUndefined = !this.chat; // Current input value from parent
        const inputChatRoomIsUndefined = !this.chatRoom; // Current input value from parent
        
        // If all inputs are undefined and parent explicitly cleared them, reset to picker mode
        if (inputUserIsUndefined && inputChatIsUndefined && inputChatRoomIsUndefined && (inputUserCleared || inputChatCleared)) {
            // Reset component state when parent clears all inputs (New button clicked)
            this.resetComponentState();
            return;
        }
        
        // If all inputs are undefined but we have internal user, check if we should keep it
        // Only keep internal user if reset trigger hasn't been set (meaning user wants to keep the selection)
        if (inputUserIsUndefined && inputChatIsUndefined && inputChatRoomIsUndefined && this.internalUser && (!this.resetTrigger || this.resetTrigger === 0)) {
            // We're in picker mode with an internally selected user - this is valid
            this.isNewChat = true;
            return;
        }
        
        // If reset trigger is set but we still have internal user, clear it
        if (this.resetTrigger && this.resetTrigger > 0 && this.internalUser) {
            this.internalUser = undefined;
            this.user = undefined;
        }
        
        // If input user is provided, clear internal user and use input
        if (this.user && this.user !== this.internalUser) {
            this.internalUser = undefined;
        }
        
        // If input user is null/undefined but we have an internal user, restore it
        // This preserves the selected user when parent passes null (e.g., when selectedChat is null)
        if (!this.user && this.internalUser && (!this.resetTrigger || this.resetTrigger === 0)) {
            this.user = this.internalUser;
        }

        // Check if user was cleared but chat still exists (shouldn't happen, but handle it)
        const userInputCleared = changes['user'] && !this.user && changes['user'].previousValue;
        if (userInputCleared && this.chat) {
            // If chat exists, use chat's user
            if (this.chat.user) {
                this.user = this.chat.user;
                this.previousUserId = this.user.id;
                this.isNewChat = false;
                if (this.messages.length === 0) {
                    this.messagesPage = 1;
                    this.loadMessages();
                }
            }
            return;
        }

        // Check if userId has changed
        if (changes['user'] && this.user?.id !== this.previousUserId) {
            // Clear previous messages and reset state
            this.messages = [];
            this.messageText = '';
            this.messagesPage = 1;
            this.showLoadMoreMessages = false;
            this.replyingToMessage = null;
            this.replyingToAttachment = null;
            this.resetAttachmentState();
            this.hasAttemptedLoad = false; // Reset load attempt flag when user changes
            
            // Update previous userId
            this.previousUserId = this.user?.id ?? null;
            
            // Update new chat state: if there's no chat, it's a new chat
            // This works for both profile context (user exists, no chat) and picker mode (no user, no chat)
            this.isNewChat = !this.chat;
            
            // Load messages for the new user
            if (this.user) {
                this.loadMessages();
            }
        } else if (this.user && this.messages.length === 0 && !this.hasAttemptedLoad && !this.loadingMessages) {
            // Fallback: if no messages and userId is set, load them (only if we haven't tried yet)
            this.messagesPage = 1;
            this.loadMessages();
        }

        // Check if chat changed
        if (changes['chat']) {
            // Update new chat state: if there's no chat, it's a new chat
            // This works for both profile context (user exists, no chat) and picker mode (no user, no chat)
            this.isNewChat = !this.chat;
            
            // If chat was cleared (parent clicked "New"), reset to picker mode
            if (!this.chat && changes['chat'].previousValue) {
                // Chat was explicitly cleared - reset component
                this.resetComponentState();
                return;
            }
            
            // Check if chat ID changed (switching between chats or chat was just created)
            const chatIdChanged = this.chat?.id !== this.previousChatId;
            // Check if chat was just set for the first time (from undefined/null to having a value)
            const chatWasJustCreated = !changes['chat'].previousValue && !!this.chat?.id;
            
            // If chat is set and has a user, sync the user
            if (this.chat?.user) {
                const userChanged = this.chat.user.id !== this.user?.id;
                
                if (userChanged) {
                    // User changed - update user and clear internal state
                    this.user = this.chat.user;
                    this.internalUser = undefined; // Clear internal user when chat provides user
                    this.previousUserId = this.user.id;
                    this.isNewChat = false;
                } else if (chatIdChanged) {
                    // Same user but different chat - clear internal user and reload messages
                    this.internalUser = undefined;
                    this.isNewChat = false;
                }
                
                // Update previous chat ID
                this.previousChatId = this.chat.id;
                
                // If chat ID changed or chat was just created, reload messages
                // But skip if we already have messages (e.g., from a recent send operation)
                if ((chatIdChanged || chatWasJustCreated) && this.user) {
                    // Only reload if we don't already have messages for this chat
                    // This prevents redundant GET requests when messages were just received from PUT response
                    const shouldReload = this.messages.length === 0 || 
                                       !this.messages.some(m => m.chat_id === this.chat.id);
                    
                    if (shouldReload) {
                        this.stopNewMessagesPoll();
                        this.messages = [];
                        this.messageText = '';
                        this.messagesPage = 1;
                        this.showLoadMoreMessages = false;
                        this.replyingToMessage = null;
                        this.replyingToAttachment = null;
                        this.resetAttachmentState();
                        this.hasAttemptedLoad = false; // Reset load attempt flag when chat changes
                        this.loadMessages();
                    }
                }
            } else if (chatIdChanged || chatWasJustCreated) {
                // Chat changed but no user - update chat ID tracking
                // If we have a user from input, try to load messages
                this.previousChatId = this.chat?.id ?? null;
                if (this.chat?.id && this.user?.id && this.context === 'profile') {
                    // Chat was just created, load messages only if we don't already have them
                    // This prevents redundant GET requests when messages were just received from PUT response
                    const shouldReload = this.messages.length === 0 || 
                                       !this.messages.some(m => m.chat_id === this.chat.id);
                    
                    if (shouldReload) {
                        this.stopNewMessagesPoll();
                        this.messages = [];
                        this.messageText = '';
                        this.messagesPage = 1;
                        this.showLoadMoreMessages = false;
                        this.replyingToMessage = null;
                        this.replyingToAttachment = null;
                        this.resetAttachmentState();
                        this.hasAttemptedLoad = false;
                        this.loadMessages();
                    }
                }
            }
        }
    }

    ngOnDestroy(): void {
        this.stopNewMessagesPoll();
    }

    async loadMessages(): Promise<void> {
        if (this.loadingMessages || !this.source) return;
        if (this.source === 'broadcast') {
            this.loadingMessages = false;
            return;
        }

        this.loadingMessages = true;
        this.hasAttemptedLoad = true; // Mark that we've attempted to load

        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat':
                    if (!this.chat?.id) {
                        this.loadingMessages = false;
                        return;
                    }
                    response = await this.resolutionCenterService.getContactMessages(this.chat.id, this.messagesPage);
                    break;

                case 'chatRoom':
                    if (!this.chatRoom?.id) {
                        this.loadingMessages = false;
                        return;
                    }
                    response = await this.resolutionCenterService.getChatRoomMessages(this.chatRoom.id, this.messagesPage);
                    break;

                default:
                    this.loadingMessages = false;
                    return;
            }

            if (!response?.error && response?.data) {
                // Handle both empty array and valid data
                const messagesData = response.data.data || [];
                this.messages = [...messagesData].sort((a, b) => a.id - b.id);
                this.showLoadMoreMessages = response.data.current_page < response.data.last_page;
                setTimeout(() => {
                    this.scrollToBottom();
                }, 100);

                // Refresh scheduled messages when we load the first page
                if (this.messagesPage === 1) {
                    this.loadScheduledMessages();
                    this.startNewMessagesPoll();
                }
            } else {
                this.notifications.error('Error', 'Failed to load messages');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to load messages');
        } finally {
            this.loadingMessages = false;
        }
    }

    /**
     * Fetches the latest messages (page 1 = newest) and merges them into the current list:
     * updates existing messages by id, appends new ones. Does not replace or reset the list.
     * If there is more than one page of new messages (e.g. many arrived while away), keeps
     * fetching and merging until we get a page that is not full or has no next page.
     */
    async fetchNewMessages(): Promise<void> {
        if (this.loadingMessages || !this.source) return;
        if (this.source === 'broadcast') return;
        if ((this.source === 'profile' || this.source === 'chat') && !this.chat?.id) return;
        if (this.source === 'chatRoom' && !this.chatRoom?.id) return;

        const previousMaxId = this.messages.length ? Math.max(...this.messages.map(m => m.id)) : 0;
        let page = 1;
        let hasMoreNewPages = true;

        while (hasMoreNewPages) {
            let response: any;
            try {
                switch (this.source) {
                    case 'profile':
                    case 'chat':
                        response = await this.resolutionCenterService.getContactMessages(this.chat!.id, page);
                        break;
                    case 'chatRoom':
                        response = await this.resolutionCenterService.getChatRoomMessages(this.chatRoom!.id, page);
                        break;
                    default:
                        return;
                }
            } catch {
                return;
            }

            if (response?.error || !response?.data) return;

            const list = response.data;
            const pageData = list.data || [];
            const perPage = list.per_page ?? 15;
            const currentPage = list.current_page ?? 1;
            const lastPage = list.last_page ?? 1;

            if (pageData.length > 0) {
                this.mergeFetchedMessagesIntoList(pageData as (ClientMessage | ChatRoomMessage)[]);
            }

            // More than one page of new messages: if this page is full and there is a next page,
            // and the oldest id on this page is still above our previous max, next page may contain more new messages.
            const minIdOnPage = pageData.length ? Math.min(...pageData.map((m: { id: number }) => m.id)) : 0;
            const pageIsFull = pageData.length >= perPage;
            const hasNextPage = currentPage < lastPage;

            if (!pageIsFull || !hasNextPage || minIdOnPage <= previousMaxId) {
                hasMoreNewPages = false;
            } else {
                page++;
            }
        }
    }

    /**
     * Merges a page of fetched messages into the current list: replace by id (update existing)
     * and add messages that are not present. Preserves order (sorted by id).
     */
    private mergeFetchedMessagesIntoList(fetched: (ClientMessage | ChatRoomMessage)[]): void {
        const byId = new Map(this.messages.map(m => [m.id, m]));
        for (const msg of fetched) {
            byId.set(msg.id, { ...msg } as ClientMessage);
        }
        this.messages = [...byId.values()].sort((a, b) => a.id - b.id);
    }

    private startNewMessagesPoll(): void {
        this.stopNewMessagesPoll();
        this.newMessagesPollIntervalId = setInterval(() => {
            this.fetchNewMessages();
        }, this.NEW_MESSAGES_POLL_INTERVAL_MS);
    }

    private stopNewMessagesPoll(): void {
        if (this.newMessagesPollIntervalId != null) {
            clearInterval(this.newMessagesPollIntervalId);
            this.newMessagesPollIntervalId = null;
        }
    }

    async loadMoreMessages(): Promise<void> {
        if (this.loadingMessages || !this.source) return;
        if (this.source === 'broadcast') return;

        // Preserve current scroll position so user stays in place after older messages are loaded
        const container: HTMLElement | null = this.messagesList?.nativeElement || null;
        const previousScrollTop = container ? container.scrollTop : 0;
        const previousScrollHeight = container ? container.scrollHeight : 0;

        this.messagesPage++;
        this.loadingMessages = true;
        
        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat':
                    if (!this.chat?.id) {
                        this.loadingMessages = false;
                        return;
                    }
                    response = await this.resolutionCenterService.getContactMessages(this.chat.id, this.messagesPage);
                    break;

                case 'chatRoom':
                    if (!this.chatRoom?.id) return;
                    response = await this.resolutionCenterService.getChatRoomMessages(this.chatRoom.id, this.messagesPage);
                    break;

                default:
                    this.messagesPage--;
                    this.loadingMessages = false;
                    return;
            }

            if (!response?.error && response?.data) {
                const newMessages = response.data.data;
                if (newMessages.length > 0) {
                    this.messages = [...this.messages, ...newMessages].sort((a, b) => a.id - b.id);
                    this.showLoadMoreMessages = response.data.current_page < response.data.last_page;

                    // After DOM update, adjust scrollTop by the height of newly prepended content
                    if (container) {
                        setTimeout(() => {
                            const element = this.messagesList?.nativeElement as HTMLElement | undefined;
                            if (!element) return;
                            const newScrollHeight = element.scrollHeight;
                            const heightDiff = newScrollHeight - previousScrollHeight;
                            element.scrollTop = previousScrollTop + heightDiff;
                        }, 0);
                    }
                } else {
                    this.messagesPage--;
                    this.notifications.warning('No messages', 'No more messages');
                }
            }
        } catch (error) {
            this.messagesPage--;
            this.notifications.error('Error', 'Failed to load more messages');
        } finally {
            this.loadingMessages = false;
        }
    }

    async sendMessage(): Promise<void> {
        if (this.isMessageSendDisabled() || !this.source) {
            return;
        }

        this.sending = true;
        const attachmentIds = this.uploadedFiles.map(file => file.id);
        const isReply = !!this.replyingToMessage;

        try {
            if (isReply) {
                // Reply logic is the same for all sources
                const response = await this.reply(attachmentIds);
                
                if (!response.error) {
                    this.handleMessageSendSuccess();
                    if (response.data) {
                        this.upsertParentMessage(response.data);
                    }
                } else {
                    this.notifications.error('Error', 'Failed to send message');
                }
            } else {
                const scheduledAt = this.scheduledDateTime;
                const response = await this.send(attachmentIds);

                if (!response?.error && response?.data) {
                    this.handleMessageSendSuccess();

                    // Show scheduled notification only when a schedule was set and send succeeded
                    if (scheduledAt) {
                        const formattedDate = this.formatScheduledDate(scheduledAt);
                        this.notifications.success(
                            'Message scheduled',
                            `Your message has been scheduled and will be delivered at ${formattedDate}.`
                        );
                    }

                    if (this.source === 'broadcast') {
                        const bm = response.data as BroadcastMessage;
                        if (scheduledAt) {
                            await this.loadScheduledMessages();
                        } else if (bm?.id != null) {
                            this.broadcastMessageUpserted.emit(bm);
                        }
                    } else {
                        // Use the returned messages directly instead of making another API call
                        const returnedMessages = response.data.data || [];
                        if (returnedMessages.length > 0) {
                            // Extract chat_id from the first message (all messages in a chat have the same chat_id)
                            const chatId = returnedMessages[0].chat_id;

                            // Update previousChatId to match the chatId from response
                            if (chatId) {
                                this.previousChatId = chatId;
                            }

                            // Only emit chatCreated if this is truly a new chat (no chat existed before)
                            // Emit for both 'profile' and 'chat' contexts when a new chat is created
                            if (this.isNewChat && !this.chat && chatId && (this.context === 'profile' || this.context === 'chat')) {
                                // This is a brand new chat - emit to trigger parent refresh
                                this.chatCreated.emit(true);
                                this.isNewChat = false;
                            }

                            // Update messages with the returned data
                            // Sort by ID to ensure correct order
                            this.messages = returnedMessages.sort((a, b) => a.id - b.id);
                            this.messagesPage = 1;
                            this.showLoadMoreMessages = response.data.current_page < response.data.last_page;

                            // Chat room: update sidebar list with new last message and timestamp
                            if (this.source === 'chatRoom' && this.chatRoom && this.messages.length > 0) {
                                const latestMessage = this.messages[this.messages.length - 1];
                                const updatedRoom = { ...this.chatRoom, last_message: latestMessage };
                                this.chatRoom = updatedRoom;
                                this.chatRoomUpdated.emit(updatedRoom);
                            }

                            // When sending a new (non-reply) message that is not scheduled,
                            // scroll the messages list to the bottom so the latest message is visible.
                            if (!scheduledAt) {
                                this.scrollToBottom();
                            }
                        } else {
                            // Fallback: if no messages returned, reload them
                            this.messagesPage = 1;
                            await this.loadMessages();
                        }
                    }
                } else {
                    this.notifications.error('Error', 'Failed to send message');
                }
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to send message');
        } finally {
            this.sending = false;
        }
    }

    private get totalQueuedAttachments(): number {
        return (this.uploadedFiles?.length || 0) + (this.fileList?.length || 0);
    }

    private isAttachmentLimitReached(): boolean {
        return this.totalQueuedAttachments >= this.MAX_ATTACHMENTS_PER_MESSAGE;
    }

    private isAttachmentTypeConflict(type: 'image' | 'video' | 'document'): boolean {
        return !!this.currentAttachmentType &&
               this.currentAttachmentType !== type &&
               this.totalQueuedAttachments > 0;
    }

    isAttachmentButtonDisabled(type: 'image' | 'video' | 'document'): boolean {
        const baseDisabled = !this.source || this.uploadingFiles || this.isEditingScheduledMessage();
        return baseDisabled || this.isAttachmentLimitReached() || this.isAttachmentTypeConflict(type);
    }

    getAttachmentButtonTitle(type: 'image' | 'video' | 'document'): string {
        if (this.isAttachmentTypeConflict(type)) {
            return `You can't send multiple attachment types`;
        }

        if (this.isAttachmentLimitReached()) {
            return `You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} files per message`;
        }

        switch (type) {
            case 'image':
                return 'Add a photo';
            case 'video':
                return 'Add a video';
            case 'document':
                return 'Add a document';
            default:
                return '';
        }
    }

    private ensureAttachmentType(type: 'image' | 'video' | 'document'): boolean {
        if (!this.currentAttachmentType) {
            this.currentAttachmentType = type;
            return true;
        }

        if (this.currentAttachmentType !== type) {
            this.notifications.warning('Attachments limited', `You can't send multiple attachment types`);
            return false;
        }

        return true;
    }

    private getRemainingAttachmentSlots(): number {
        return this.MAX_ATTACHMENTS_PER_MESSAGE - this.totalQueuedAttachments;
    }

    async uploadFiles(): Promise<void> {
        if (this.fileList.length === 0) {
            this.uploadingFiles = false;
            this.uploadProgress = 0;
            return;
        }

        this.uploadingFiles = true;
        const upload = async () => {
            if (this._uploadedCounter < this.fileList.length) {
                const file = this.fileList[this._uploadedCounter];
                this.currentUploadIndex = this._uploadedCounter;
                
                try {
                    const uploadObservable = await this.resolutionCenterService.uploadAttachment(file.name, file, 0);
                    uploadObservable.subscribe(
                        res => {
                            if (res.progress !== undefined) {
                                this.uploadProgress = res.progress;
                            } else if (res.response) {
                                this.uploadProgress = 0;
                                if (res.response && res.response.data) {
                                    this.uploadedFiles.push(res.response.data);
                                }
                                this._uploadedCounter++;
                                upload();
                            }
                            // Ignore res.error - "Unhandled event" messages are normal HTTP events, not real errors
                        },
                        error => {
                            this.uploadProgress = 0;
                            this._uploadedCounter++;
                            const err = error.status === 502 ? error.statusText : (error.error?.errorMessages?.join('\r\n') || 'Upload failed');
                            this.notifications.error('Error', err);
                            upload();
                        },
                        () => {
                            // Complete handler - continue with next upload if needed
                            // The response handler already calls upload(), so this is just a safety net
                        }
                    );
                } catch (error) {
                    this.uploadProgress = 0;
                    this._uploadedCounter++;
                    this.notifications.error('Error', 'Failed to start upload');
                    upload();
                }
            } else {
                this.fileList = [];
                this._uploadedCounter = 0;
                this.uploadingFiles = false;
                this.uploadProgress = 0;
            }
        };

        upload();
    }

    async togglePinned(): Promise<void> {
        if (this.source === 'chatRoom') {
            if (!this.chatRoom?.id) return;
            try {
                const { data, error } = await this.resolutionCenterService.toggleChatRoomIsPinned(this.chatRoom.id);
                if (error) {
                    this.notifications.error('Error', 'Failed to toggle pin status');
                    return;
                }
                this.updateChatRoomFlags(data ?? { is_pinned: !this.isPinned });
            } catch (err) {
                this.notifications.error('Error', 'Failed to toggle pin status');
            }
        } else {
            if (!this.chat?.id) return;
            try {
                const { data, error } = await this.resolutionCenterService.toggleChatIsPinned(this.chat.id, this.getChatApplicationId());
                if (error) {
                    this.notifications.error('Error', 'Failed to toggle pin status');
                    return;
                }
                this.updateChatFlags(data ?? { is_pinned: !this.isPinned });
            } catch (err) {
                this.notifications.error('Error', 'Failed to toggle pin status');
            }
        }
    }

    async toggleFavorited(): Promise<void> {
        if (this.source === 'chatRoom') {
            if (!this.chatRoom?.id) return;
            try {
                const { data, error } = await this.resolutionCenterService.toggleChatRoomIsFavorited(this.chatRoom.id);
                if (error) {
                    this.notifications.error('Error', 'Failed to toggle favorite status');
                    return;
                }
                const nextFavorite = !this.isFavorited;
                this.updateChatRoomFlags(data ?? { is_favorited: nextFavorite });
            } catch (err) {
                this.notifications.error('Error', 'Failed to toggle favorite status');
            }
        } else {
            if (!this.chat?.id) return;
            try {
                const { data, error } = await this.resolutionCenterService.toggleChatIsFavorited(this.chat.id, this.getChatApplicationId());
                if (error) {
                    this.notifications.error('Error', 'Failed to toggle favorite status');
                    return;
                }
                const nextFavorite = !this.isFavorited;
                this.updateChatFlags(data ?? { is_favorited: nextFavorite });
            } catch (err) {
                this.notifications.error('Error', 'Failed to toggle favorite status');
            }
        }
    }

    async toggleArchived(): Promise<void> {
        if (this.source === 'chatRoom') {
            if (!this.chatRoom?.id) return;
            try {
                const { data, error } = await this.resolutionCenterService.toggleChatRoomIsArchived(this.chatRoom.id);
                if (error) {
                    this.notifications.error('Error', 'Failed to toggle archive status');
                    return;
                }
                this.updateChatRoomFlags(data ?? { is_archived: !this.isArchived });
            } catch (err) {
                this.notifications.error('Error', 'Failed to toggle archive status');
            }
        } else {
            if (!this.chat?.id) return;
            try {
                const { data, error } = await this.resolutionCenterService.toggleChatsArchived(this.chat.id, this.getChatApplicationId());
                if (error) {
                    this.notifications.error('Error', 'Failed to toggle archive status');
                    return;
                }
                this.updateChatFlags(data ?? { is_archived: !this.isArchived });
            } catch (err) {
                this.notifications.error('Error', 'Failed to toggle archive status');
            }
        }
    }

    canManageCustomChatRoom(): boolean {
        if (this.source !== 'chatRoom' || !this.chatRoom || this.chatRoom.type !== 'custom') return false;
        if (!this.isCoach) return true; //owner and admin can manage custom chat rooms
        return this.chatRoom.is_owner;
    }

    canEditCustomChatRoom(): boolean {
        if (this.canManageCustomChatRoom()) return false;
        if (this.source !== 'chatRoom' || !this.chatRoom || this.chatRoom.type !== 'custom') return false;
        if (!this.isCoach) return false; //owner and admin can manage custom chat rooms
        return !this.chatRoom.is_owner;
    }

    async toggleReaction(message: ClientMessage, reaction: string): Promise<void> {
        try {
            let response: any;
            switch (this.source) {
                case 'profile':
                case 'chat':
                    response = await this.resolutionCenterService.toggleChatMessageReaction(message.id, reaction);
                    break;
                case 'chatRoom':
                    response = await this.resolutionCenterService.toggleChatRoomMessageReaction(message.id, reaction);
                    break;
                default:
                    return;
            }
            
            if (!response.error) {
                // Update the message's reactions locally
                const messageToUpdate = this.findMessageById(message.id);
                if (messageToUpdate) {
                    messageToUpdate.reactions = response.data.reactions;
                }
                // Also update the message passed in (for thread messages)
                message.reactions = response.data.reactions;
                // Close the picker after selecting a reaction
                this.showReactionPicker = null;
            } else {
                this.notifications.error('Error', 'Failed to toggle reaction');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to toggle reaction');
        }
    }

    async toggleAttachmentReaction(attachment: Attachment, reaction: string): Promise<void> {
        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat':
                    response = await this.resolutionCenterService.toggleChatMessageAttachmentReaction(
                        attachment.chat_message_attachment_id,
                        reaction
                    );
                    break;
                case 'chatRoom':
                    response = await this.resolutionCenterService.toggleChatRoomMessageAttachmentReaction(
                        attachment.chat_message_attachment_id,
                        reaction
                    );
                    break;
                default:
                    return;
            }

            if (!response.error) {
                this.replaceAttachmentInMessages(response.data);
                attachment.reactions = response.data.reactions;
                this.showAttachmentReactionPicker = null;
            } else {
                this.notifications.error('Error', 'Failed to toggle reaction');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to toggle reaction');
        }
    }

    getTotalReactions(breakdown: ReactionsBreakdown | null): number {
        if (!breakdown?.reactions || breakdown.reactions.length === 0) {
            return 0;
        }

        return breakdown.reactions.reduce((sum, reaction) => sum + (reaction.count || 0), 0);
    }

    async openMessageReactions(message: ClientMessage): Promise<void> {
        if (!message?.id) {
            return;
        }

        this.showReactionsBreakdown = true;
        this.reactionsBreakdownLoading = true;
        this.reactionsBreakdown = null;

        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat':
                    response = await this.resolutionCenterService.getChatMessageReactions(message.id);
                    break;
                case 'chatRoom':
                    response = await this.resolutionCenterService.getChatRoomMessageReactions(message.id);
                    break;
                default:
                    this.showReactionsBreakdown = false;
                    this.reactionsBreakdownLoading = false;
                    return;
            }

            if (!response.error && response.data) {
                this.reactionsBreakdown = response.data as ReactionsBreakdown;
            } else {
                this.notifications.error('Error', 'Failed to load reactions');
                this.showReactionsBreakdown = false;
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to load reactions');
            this.showReactionsBreakdown = false;
        } finally {
            this.reactionsBreakdownLoading = false;
        }
    }

    async openAttachmentReactions(attachment: Attachment): Promise<void> {
        if (!attachment?.chat_message_attachment_id) {
            return;
        }

        this.showReactionsBreakdown = true;
        this.reactionsBreakdownLoading = true;
        this.reactionsBreakdown = null;

        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat':
                    response = await this.resolutionCenterService.getChatMessageAttachmentReactions(
                        attachment.chat_message_attachment_id
                    );
                    break;
                case 'chatRoom':
                    response = await this.resolutionCenterService.getChatRoomMessageAttachmentReactions(
                        attachment.chat_message_attachment_id
                    );
                    break;
                default:
                    this.showReactionsBreakdown = false;
                    this.reactionsBreakdownLoading = false;
                    return;
            }

            if (!response.error && response.data) {
                this.reactionsBreakdown = response.data as ReactionsBreakdown;
            } else {
                this.notifications.error('Error', 'Failed to load reactions');
                this.showReactionsBreakdown = false;
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to load reactions');
            this.showReactionsBreakdown = false;
        } finally {
            this.reactionsBreakdownLoading = false;
        }
    }

    closeReactionsBreakdown(): void {
        this.showReactionsBreakdown = false;
        this.reactionsBreakdown = null;
    }

    async searchUsers(query: string): Promise<void> {
        if (!query || query.trim().length === 0) {
            this.userSearchResults = [];
            return;
        }

        // Store focus state before search
        const shouldRefocus = this.wasInputFocused && 
            this.userSearchInput?.nativeElement && 
            document.activeElement === this.userSearchInput.nativeElement;

        this.searchingUsers = true;
        try {
            const response = await this.resolutionCenterService.getContacts(1, query.trim());
            if (!response.error && response.data) {
                this.userSearchResults = response.data.data || [];
            } else {
                this.userSearchResults = [];
            }
        } catch (error) {
            this.userSearchResults = [];
            this.notifications.error('Error', 'Failed to search users');
        } finally {
            this.searchingUsers = false;
            // Use requestAnimationFrame to ensure DOM is updated before refocusing
            if (shouldRefocus && this.userSearchInput?.nativeElement) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (this.userSearchInput?.nativeElement) {
                            this.userSearchInput.nativeElement.focus();
                            // Also restore cursor position if possible
                            const input = this.userSearchInput.nativeElement;
                            const cursorPosition = this.userSearchQuery.length;
                            if (input.setSelectionRange) {
                                input.setSelectionRange(cursorPosition, cursorPosition);
                            }
                        }
                    });
                });
            }
        }
    }

    async deleteAttachment(attachment: Attachment): Promise<void> {
        const confirmed = await this.interaction.confirm({
            title: 'Delete message?',
            message: 'Are you sure you want to delete this message? This action is permanent and cannot be undone.',
            buttons: [
                {
                    label: 'Cancel',
                    value: ConfirmStatus.CANCELED,
                    style: 'default'
                },
                {
                    label: 'Delete message',
                    value: ConfirmStatus.CONFIRMED,
                    style: 'danger'
                }
            ]
        });
        if (!confirmed) return;

        try {
            let response: any;
            
            switch (this.source) {
                case 'profile':
                case 'chat':
                    response = await this.resolutionCenterService.deleteAttachment(attachment, null, true);
                    break;
                case 'chatRoom':
                    response = await this.resolutionCenterService.deleteChatRoomAttachment(attachment);
                    break;
            }

            if (!response.error) {
                this.notifications.success('Success', 'Attachment deleted');
                await this.loadMessages();
            } else {
                this.notifications.error('Error', 'Failed to delete attachment');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to delete attachment');
        }
    }

    async deleteMessage(message: ClientMessage): Promise<void> {
        const confirmed = await this.interaction.confirm({
            title: 'Delete message?',
            message: 'Are you sure you want to delete this message? This action is permanent and cannot be undone.',
            buttons: [
                {
                    label: 'Cancel',
                    value: ConfirmStatus.CANCELED,
                    style: 'default'
                },
                {
                    label: 'Delete message',
                    value: ConfirmStatus.CONFIRMED,
                    style: 'danger'
                }
            ]
        });
        
        if (!confirmed) return;

        try {
            let response: any;
            
            switch (this.source) {
                case 'profile':
                case 'chat':
                    response = await this.resolutionCenterService.deleteMessage(message.id, null, true);
                    break;
                case 'chatRoom':
                    response = await this.resolutionCenterService.deleteChatRoomMessage(message.id);
                    break;
                default:
                    return;
            }

            if (!response.error) {
                this.notifications.success('Success', 'Message deleted');
                await this.loadMessages();
            } else {
                this.notifications.error('Error', 'Failed to delete message');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to delete message');
        }
    }

    async markMessageAsUnread(message: ClientMessage): Promise<void> {
        try {
            const response = await this.resolutionCenterService.toggleMessageReadStatus(message.id);
            if (!response.error) {
                // Update the message's unread status locally
                message.is_message_read = false;
                this.notifications.success('Success', 'Message marked as unread successfully');
            } else {
                this.notifications.error('Error', 'Failed to mark message as unread');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to mark message as unread');
        }
    }

    get isPinned(): boolean {
        if (this.source === 'chat') {
            return !!(this.chat as any)?.is_pinned;
        }
        if (this.source === 'chatRoom') {
            return !!this.chatRoom?.is_pinned;
        }
        return false;
    }

    get isFavorited(): boolean {
        if (this.source === 'chat') {
            return !!(this.chat as any)?.is_favorited;
        }
        if (this.source === 'chatRoom') {
            return !!this.chatRoom?.is_favorited;
        }
        return false;
    }

    get isArchived(): boolean {
        if (this.source === 'chat') {
            return !!(this.chat as any)?.is_archived;
        }
        if (this.source === 'chatRoom') {
            return !!this.chatRoom?.is_archived;
        }
        return false;
    }

    openUserDetails(): void {
        if (!this.user?.id) return;
        const url = `/user/user/details-mantis/${this.user.id}`;
        window.open(url, '_blank');
    }

    getUserFullName(user: ChatUser): string {
        if (!user.first_name && !user.last_name) return 'Unknown User';
        return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }

    getAuthorFirstName(author: ChatParticipant): string {
        if (!author.name) return 'Unknown';
        return author.name.split(' ')[0];
    }

    getInitials(firstName?: string, lastName?: string): string {
        if (!firstName && !lastName) return '?';
        const first = firstName ? firstName.charAt(0).toUpperCase() : '';
        const last = lastName ? lastName.charAt(0).toUpperCase() : '';
        return first + last;
    }

    getOwnerName(message: ClientMessage): string {
        return message.author?.name || 'Unknown';
    }

    getOwnerInitials(message: ClientMessage): string {
        const name = message.author?.name;
        if (!name) return '?';
        const parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        }
        return parts[0].charAt(0).toUpperCase();
    }

    getChatRoomInitials(roomName: string): string {
        if (!roomName) return '?';
        const words = roomName.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
        }
        return words[0].charAt(0).toUpperCase();
    }

    getMessageInitials(message: ClientMessage): string {
        if (!message.author.name) return '?';
        const parts = message.author.name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
        }
        return parts[0].charAt(0).toUpperCase();
    }

    /**
     * Convert URLs in a plain-text message into clickable links.
     * Returns SafeHtml used with [innerHTML] in the template.
     */
    getMessageWithLinks(message: string | null | undefined): SafeHtml | string {
        if (!message) {
            return '';
        }

        // Match http(s)://... or www....
        const urlRegex = /((https?:\/\/|www\.)[^\s]+)/gi;

        let linked = message.replace(urlRegex, (rawUrl: string) => {
            let href = rawUrl;

            // If it starts with www., prepend http:// so the link works
            if (!/^https?:\/\//i.test(href)) {
                href = 'http://' + href;
            }

            const escapedText = rawUrl
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');

            return `<a href="${href}" target="_blank" rel="noopener noreferrer">${escapedText}</a>`;
        });

        // Preserve line breaks
        linked = linked.replace(/\n/g, '<br/>');

        return this.sanitizer.bypassSecurityTrustHtml(linked);
    }

    // User picker methods for new chat creation
    onUserSearchChange(query: string): void {
        this.userSearchQuery = query;
        this.userSearchSubject.next(query);
    }

    async onUserSelected(user: ChatUser): Promise<void> {
        if (!user) return;

        // Clear search UI
        this.userSearchQuery = '';
        this.userSearchResults = [];

        // If this user already has a chat, load the existing conversation
        if (user.chat_id != null) {
            try {
                const { data, error } = await this.resolutionCenterService.getChatByUserId(user.id);
                if (!error && data) {
                    // Use the existing chat just like when selected from the left-hand list
                    this.chat = data;
                    this.user = data.user;
                    this.internalUser = undefined;
                    this.selectedUserForNewChat = null;
                    this.isNewChat = false;

                    this.previousUserId = data.user?.id ?? user.id;
                    this.previousChatId = data.id;
                    this.messagesPage = 1;
                    this.messages = [];
                    this.loadMessages();
                    // Notify parent so it can update selectedChat and right-side panel
                    this.chatUpdated.emit(this.chat as ClientChat);
                    return;
                }
            } catch {
                // If loading existing chat fails, fall back to new chat behavior below
            }
        }

        // Fallback: treat as a brand new chat with this user
        this.user = user;
        this.internalUser = user; // Track that this is an internally set user
        this.selectedUserForNewChat = user;
        this.isNewChat = true; // This is a new chat

        // Reset message state; no existing messages yet for a new chat
        this.previousUserId = user.id;
        this.messagesPage = 1;
        this.messages = [];
        this.loadMessages();
    }

    trackByCustomerId(index: number, user: ChatUser): number {
        return user.id;
    }

    getThreadReplyLabel(message: ClientMessage): string {
        // We always receive the root/original message here (the one that started the thread).
        // The "current" message that triggered the label is the first reply in the thread.
        if (!message.thread || message.thread.length === 0) return '';

        const rootMessage = message;
        const firstThreadMessage = message.thread[0];

        const rootIsMyMessage = !!rootMessage.is_my_message;
        const firstThreadIsMyMessage = !!firstThreadMessage.is_my_message;

        //const rootAuthorName = rootMessage.author?.name;
        const rootAuthorName = this.getAuthorFirstName(rootMessage.author);
        const firstThreadAuthorName = firstThreadMessage.author?.name;

        // Owner replied to user (root from user, first reply from owner)
        if (!rootIsMyMessage && firstThreadIsMyMessage) {
            const clientName = rootAuthorName || 'client';
            return `You replied to ${clientName}`;
        }

        // User replied to owner (root from owner, first reply from user)
        if (rootIsMyMessage && !firstThreadIsMyMessage) {
            const clientName = firstThreadAuthorName || 'client';
            return `${clientName} replied to you`;
        }

        // Owner replied to themselves
        if (rootIsMyMessage && firstThreadIsMyMessage) {
            return 'You replied to yourself';
        }

        // User replied to user (unlikely in this context, but handle gracefully)
        if (!rootIsMyMessage && !firstThreadIsMyMessage) {
            const clientName = firstThreadAuthorName || rootAuthorName || 'client';
            return `${clientName} replied to them`;
        }

        return '';
    }

    isMessageSendDisabled(): boolean {
        const hasSource = this.source !== null;
        return !hasSource || this.sending || this.uploadingFiles || (!this.messageText?.trim() && (!this.uploadedFiles || this.uploadedFiles.length === 0));
    }

    onAttachImageClick(): void {
        if (this.fileInput && !this.isAttachmentButtonDisabled('image')) {
            this.fileInput.nativeElement.click();
        }
    }

    onAttachVideoClick(): void {
        if (this.videoInput && !this.isAttachmentButtonDisabled('video')) {
            this.videoInput.nativeElement.click();
        }
    }

    onAttachDocumentClick(): void {
        if (this.documentInput && !this.isAttachmentButtonDisabled('document')) {
            this.documentInput.nativeElement.click();
        }
    }

    onFileSelected(event: Event, type: 'image' | 'video' | 'document'): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            const files = Array.from(input.files);

            switch (type) {
                case 'image':
                    this.onAttachedMedia(files, 'image');
                    break;
                case 'video':
                    this.onAttachedMedia(files, 'video');
                    break;
                case 'document':
                default:
                    this.onAttachedDocuments(files);
                    break;
            }

            // Reset input so same file can be selected again
            input.value = '';
        }
    }

    private onAttachedMedia(files: File[], mediaType: 'image' | 'video'): void {
        const attachmentType: 'image' | 'video' = mediaType;

        if (!this.ensureAttachmentType(attachmentType)) {
            return;
        }

        const filteredFiles = files.filter(file => {
            const type = file.type.toLowerCase();
            return mediaType === 'image'
                ? type.startsWith('image/')
                : type.startsWith('video/');
        });

        if (filteredFiles.length === 0) {
            this.notifications.warning(
                'Invalid files',
                mediaType === 'image'
                    ? 'Please select only images'
                    : 'Please select only videos'
            );
            return;
        }

        const remainingSlots = this.getRemainingAttachmentSlots();
        if (remainingSlots <= 0) {
            this.notifications.warning(
                'Attachment limit reached',
                `You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} ${mediaType === 'image' ? 'images' : 'videos'} per message`
            );
            return;
        }

        const filesToAdd = filteredFiles.slice(0, remainingSlots);
        if (filesToAdd.length < filteredFiles.length) {
            this.notifications.warning(
                'Attachment limit reached',
                `You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} ${mediaType === 'image' ? 'images' : 'videos'} per message`
            );
        }

        this.fileList = [...this.fileList, ...filesToAdd];
        this.totalUploads = this.fileList.length;
        this._uploadedCounter = 0;
        this.uploadFiles();
    }

    onAttachedDocuments(files: File[]): void {
        if (!this.ensureAttachmentType('document')) {
            return;
        }

        // Filter for allowed document types
        const allowedExtensions = ['.pdf', '.xls', '.xlsx', '.csv', '.doc', '.docx', '.txt'];
        const documentFiles = files.filter(file => {
            const fileName = file.name.toLowerCase();
            return allowedExtensions.some(ext => fileName.endsWith(ext));
        });

        if (documentFiles.length === 0) {
            this.notifications.warning('Invalid files', 'Please select only PDF, Excel, Word, CSV, or TXT files');
            return;
        }

        const remainingSlots = this.getRemainingAttachmentSlots();
        if (remainingSlots <= 0) {
            this.notifications.warning(
                'Attachment limit reached',
                `You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} documents per message`
            );
            return;
        }

        const filesToAdd = documentFiles.slice(0, remainingSlots);
        if (filesToAdd.length < documentFiles.length) {
            this.notifications.warning(
                'Attachment limit reached',
                `You can attach up to ${this.MAX_ATTACHMENTS_PER_MESSAGE} documents per message`
            );
        }

        this.fileList = [...this.fileList, ...filesToAdd];
        this.totalUploads = this.fileList.length;
        this._uploadedCounter = 0;
        this.uploadFiles();
    }

    getFileTypeIcon(file: GalleryItem): string {
        const fileName = file.name.toLowerCase();
        if (fileName.endsWith('.pdf')) return 'ti-file-text';
        if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx') || fileName.endsWith('.csv')) return 'ti-file-analytics';
        if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return 'ti-file-text';
        if (fileName.endsWith('.txt')) return 'ti-file';
        return 'ti-file';
    }

    getFileTypeClass(file: GalleryItem): string {
        const fileName = file.name.toLowerCase();
        if (fileName.endsWith('.pdf')) return 'file-type-pdf';
        if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx') || fileName.endsWith('.csv')) return 'file-type-excel';
        if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return 'file-type-word';
        if (fileName.endsWith('.txt')) return 'file-type-txt';
        return 'file-type-default';
    }

    getAttachmentIcon(attachment: Attachment): string {
        if (!attachment?.attachment) {
            return 'ti ti-file';
        }

        const mimeType = attachment.attachment.mime_type?.toLowerCase() || '';
        const extension = attachment.attachment.extension?.toLowerCase() || '';
        
        // Check mime_type first
        if (mimeType.includes('pdf')) return 'icon icon-pdf-document';
        if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return 'icon icon-excel-document';
        if (mimeType.includes('word') || mimeType.includes('document')) return 'icon icon-word-document';
        if (mimeType.includes('text/plain') || mimeType.includes('text')) return 'icon icon-text-document';
        
        // Fallback to file extension if mime_type is not available
        if (extension === 'pdf') return 'icon icon-pdf-document';
        if (extension === 'xls' || extension === 'xlsx' || extension === 'csv') return 'icon icon-excel-document';
        if (extension === 'doc' || extension === 'docx') return 'icon icon-word-document';
        if (extension === 'txt') return 'icon icon-text-document';
        
        return 'icon icon-text-document';
    }

    getAttachmentIconClass(attachment: Attachment): string {
        if (!attachment?.attachment) {
            return 'attachment-icon-default';
        }

        const mimeType = attachment.attachment.mime_type?.toLowerCase() || '';
        const extension = attachment.attachment.extension?.toLowerCase() || '';
        
        // Check mime_type first
        if (mimeType.includes('pdf')) return 'attachment-icon-pdf';
        if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType.includes('csv')) return 'attachment-icon-excel';
        if (mimeType.includes('word') || mimeType.includes('document')) return 'attachment-icon-word';
        if (mimeType.includes('text/plain') || mimeType.includes('text')) return 'attachment-icon-txt';
        
        // Fallback to file extension if mime_type is not available
        if (extension === 'pdf') return 'attachment-icon-pdf';
        if (extension === 'xls' || extension === 'xlsx' || extension === 'csv') return 'attachment-icon-excel';
        if (extension === 'doc' || extension === 'docx') return 'attachment-icon-word';
        if (extension === 'txt') return 'attachment-icon-txt';
        
        return 'attachment-icon-default';
    }

    onRemoveAttachment(attachment: GalleryItem): void {
        this.uploadedFiles = this.uploadedFiles.filter(file => file.id !== attachment.id);

        if (this.uploadedFiles.length === 0 && this.fileList.length === 0) {
            this.currentAttachmentType = null;
        }
    }

    onEmojiClick(): void {
        this.showEmojiPicker = !this.showEmojiPicker;
    }

    onEmojiSelect(event: any): void {
        if (event && event.emoji && event.emoji.native) {
            this.messageText = (this.messageText || '') + event.emoji.native;
            this.showEmojiPicker = false;
        }
    }

    closeEmojiPicker(): void {
        this.showEmojiPicker = false;
    }

    onEnterKey(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (this.isEditingScheduledMessage()) {
                this.confirmScheduledMessageEdit();
            } else {
                this.sendMessage();
            }
        }
    }

    private readonly messageInputMaxHeightPx = 120;
    private readonly messageInputMinHeightPx = 44;

    adjustMessageInputHeight(): void {
        const el = this.messageInputRef?.nativeElement;
        if (!el) return;
        el.style.height = '0';
        const scrollHeight = el.scrollHeight;
        const height = Math.min(Math.max(scrollHeight, this.messageInputMinHeightPx), this.messageInputMaxHeightPx);
        el.style.height = `${height}px`;
    }

    shouldShowDateSeparator(message: ClientMessage, index: number): boolean {
        if (index === 0) return true;
        const currentDate = new Date(message.created_at).toDateString();
        const previousDate = new Date(this.messages[index - 1].created_at).toDateString();
        return currentDate !== previousDate;
    }

    getDateSeparatorLabel(message: ClientMessage): string {
        const messageDate = new Date(message.created_at);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        // Reset time to compare only dates
        const messageDateOnly = new Date(messageDate.getFullYear(), messageDate.getMonth(), messageDate.getDate());
        const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

        if (messageDateOnly.getTime() === todayOnly.getTime()) {
            return 'Today';
        } else if (messageDateOnly.getTime() === yesterdayOnly.getTime()) {
            return 'Yesterday';
        } else {
            // Format as "Monday, Nov 11 2025"
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            
            const dayName = days[messageDate.getDay()];
            const monthName = months[messageDate.getMonth()];
            const day = messageDate.getDate();
            const year = messageDate.getFullYear();
            
            return `${dayName}, ${monthName} ${day} ${year}`;
        }
    }

    getTimeAgo(dateString: string): string {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
        
        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) {
            const minutes = Math.floor(diffInSeconds / 60);
            return `${minutes}m ago`;
        }
        if (diffInSeconds < 86400) {
            const hours = Math.floor(diffInSeconds / 3600);
            return `${hours}h ago`;
        }
        const days = Math.floor(diffInSeconds / 86400);
        return `${days}d ago`;
    }

    isEditingScheduledMessage(): boolean {
        return !!this.scheduledMessageBeingEdited;
    }

    scrollToBottom(): void {
        if (this.messagesList) {
            const element = this.messagesList.nativeElement;
            setTimeout(() => {
                element.scrollTop = element.scrollHeight;
            }, 100);
        }
    }

    replyToMessage(message: ClientMessage, attachment?: Attachment): void {
        this.replyingToMessage = message;
        this.replyingToAttachment = attachment || null;
    }

    findMessageById(id: number): ClientMessage | null {
        // Search in main messages
        const found = this.messages.find(m => m.id === id);
        if (found) return found;
        
        // Search in thread arrays
        for (const message of this.messages) {
            if (message.thread && message.thread.length > 0) {
                const foundInThread = message.thread.find(t => t.id === id);
                if (foundInThread) return foundInThread;
            }
        }
        
        return null;
    }

    private async loadScheduledMessages(): Promise<void> {
        // Clear when there is no active source
        if (!this.source) {
            this.scheduledMessages = [];
            return;
        }

        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat':
                    if (!this.chat?.id) {
                        this.scheduledMessages = [];
                        return;
                    }
                    response = await this.resolutionCenterService.getScheduledChatMessages(this.chat.id);
                    break;

                case 'chatRoom':
                    if (!this.chatRoom?.id) {
                        this.scheduledMessages = [];
                        return;
                    }
                    response = await this.resolutionCenterService.getScheduledChatRoomMessages(this.chatRoom.id);
                    break;

                case 'broadcast':
                    if (!this.broadcastAudience?.audience_key) {
                        this.scheduledMessages = [];
                        return;
                    }
                    response = await this.resolutionCenterService.getScheduledBroadcastMessages(
                        this.broadcastAudience.audience_key,
                    );
                    break;

                default:
                    this.scheduledMessages = [];
                    return;
            }

            if (!response?.error && response?.data) {
                this.scheduledMessages = response.data;
            } else {
                this.scheduledMessages = [];
            }
        } catch (error) {
            // For now, fail silently – this panel is auxiliary
            this.scheduledMessages = [];
        }
    }

    private upsertParentMessage(updatedParent: ClientMessage): void {
        const index = this.messages.findIndex(m => m.id === updatedParent.id);
        if (index !== -1) {
            this.messages[index] = { ...updatedParent };
        } else {
            this.messages.push({ ...updatedParent });
            this.messages.sort((a, b) => a.id - b.id);
        }
        this.messages = [...this.messages];
    }

    private handleMessageSendSuccess(): void {
        this.messageText = '';
        this.resetAttachmentState();
        this.replyingToAttachment = null;
        this.scheduledDateTime = null;
        this.cancelReply();
        setTimeout(() => this.adjustMessageInputHeight(), 0);

        // After a successful send, refresh scheduled messages to reflect any changes
        if (this.source === 'broadcast' || this.messagesPage === 1) {
            void this.loadScheduledMessages();
        }
    }

    cancelReply(): void {
        this.replyingToMessage = null;
        this.replyingToAttachment = null;
    }

    isObjectAttachment(attachment: Attachment): boolean {
        return isObjectTypeAttachment(attachment) && !!attachment.object;
    }

    getObjectAttachmentLabel(attachment: Attachment): string {
        return getChatObjectAttachmentLabel(attachment.object_type || '', 1);
    }

    getObjectAttachmentIcon(attachment: Attachment): string | null {
        return getObjectAttachmentIconPath(attachment.object_type || '');
    }

    getObjectAttachmentName(attachment: Attachment): string {
        return getObjectAttachmentNameValue(attachment);
    }

    getObjectAttachmentImageUrl(attachment: Attachment): string | null {
        return getObjectAttachmentImageUrlValue(attachment);
    }

    getObjectAttachmentMeta(attachment: Attachment): string {
        return getObjectAttachmentMetaValue(attachment);
    }

    isImageAttachment(attachment: Attachment): boolean {
        if (!isChatFileAttachment(attachment) || !attachment?.attachment) return false;
        const type = attachment.attachment.type?.toLowerCase();
        const name = attachment.attachment.name?.toLowerCase() || '';
        const url = attachment.attachment.url?.toLowerCase() || '';
        
        // Check type field (including 'gif' for GIF attachments)
        if (type === 'image' || type === 'gif') return true;
        
        // Check if it's a Giphy URL (GIF attachment)
        if (url.includes('giphy.com') || url.includes('i.giphy.com')) return true;
        
        // Check file extension
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'];
        const hasImageExtension = imageExtensions.some(ext => 
            name.endsWith(ext) || url.endsWith(ext)
        );
        if (hasImageExtension) return true;
        
        // Check mime type if available
        if (attachment.attachment.type && attachment.attachment.type.startsWith('image/')) {
            return true;
        }
        
        return false;
    }

    hasImageAttachments(message: ClientMessage): boolean {
        if (!message.attachments || message.attachments.length === 0) return false;
        return message.attachments.some(attachment => this.isImageAttachment(attachment));
    }

    hasVideoAttachments(message: ClientMessage): boolean {
        if (!message.attachments || message.attachments.length === 0) return false;
        return message.attachments.some(attachment => this.isVideoAttachment(attachment));
    }

    isGifAttachment(attachment: Attachment): boolean {
        if (!isChatFileAttachment(attachment) || !attachment?.attachment) return false;
        const type = attachment.attachment.type?.toLowerCase();
        const url = attachment.attachment.url?.toLowerCase() || '';
        
        // Check if it's a Giphy URL (GIF attachment)
        if (url.includes('giphy.com') || url.includes('i.giphy.com')) return true;
        
        // Check type field
        if (type === 'gif') return true;
        
        // Check mime type
        if (attachment.attachment.mime_type?.toLowerCase() === 'image/gif') return true;
        
        // Check file extension
        if (url.endsWith('.gif')) return true;
        
        return false;
    }

    isVideoAttachment(attachment: Attachment): boolean {
        if (!isChatFileAttachment(attachment) || !attachment?.attachment) return false;
        const type = attachment.attachment.type?.toLowerCase();
        const mimeType = attachment.attachment.mime_type?.toLowerCase() || '';
        const name = attachment.attachment.name?.toLowerCase() || '';
        const url = attachment.attachment.url?.toLowerCase() || '';
        
        // Check type field
        if (type === 'video') return true;
        
        // Check mime type
        if (mimeType.startsWith('video/')) return true;
        
        // Check file extension
        const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm', '.mkv'];
        const hasVideoExtension = videoExtensions.some(ext => 
            name.endsWith(ext) || url.endsWith(ext)
        );
        if (hasVideoExtension) return true;
        
        return false;
    }

    openMessageImageModal(attachment: Attachment): void {
        if (!attachment || !attachment.attachment) return;
        this.selectedImage = {
            image_url: attachment.attachment.url,
            label: attachment.attachment.name || 'Message image',
            display_date: new Date().toISOString()
        };
        this.isClosingImageModal = false;
        this.showImageModal = true;
    }

    closeImageModal(): void {
        this.isClosingImageModal = true;
        setTimeout(() => {
            this.showImageModal = false;
            this.isClosingImageModal = false;
            this.selectedImage = null;
        }, 180);
    }

    isVideoFile(image: any): boolean {
        if (!image || !image.image_url) return false;
        const url = image.image_url.toLowerCase();
        const videoExtensions = ['.mp4', '.mov', '.avi', '.wmv', '.flv', '.webm', '.mkv'];
        return videoExtensions.some(ext => url.includes(ext) || url.endsWith(ext));
    }

    onGifClick(): void {
        if (this.context === 'broadcast') return;
        this.showGifPicker = !this.showGifPicker;
        if (this.showGifPicker && this.gifs.length === 0) {
            this.loadTrendingGifs();
        }
    }

    onGifSearchChange(query: string): void {
        this.gifSearchQuery = query || '';
        this.gifOffset = 0;
        this.gifs = [];
        this.hasMoreGifs = true;
        this.gifSearchSubject.next(query || '');
    }

    async loadTrendingGifs(): Promise<void> {
        if (this.loadingGifs || !environment.giphyApiKey || environment.giphyApiKey === 'YOUR_GIPHY_API_KEY') {
            if (!environment.giphyApiKey || environment.giphyApiKey === 'YOUR_GIPHY_API_KEY') {
                this.notifications.warning('Configuration', 'Please configure Giphy API key in environment files');
            }
            return;
        }

        this.loadingGifs = true;
        try {
            const url = `https://api.giphy.com/v1/gifs/trending?api_key=${environment.giphyApiKey}&limit=25&offset=${this.gifOffset}`;
            const response: any = await this.http.get(url).toPromise();
            
            if (response && response.data) {
                this.gifs = [...this.gifs, ...response.data];
                this.hasMoreGifs = response.pagination && response.pagination.count === 25;
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to load GIFs');
        } finally {
            this.loadingGifs = false;
        }
    }

    async searchGifs(query: string): Promise<void> {
        if (this.loadingGifs || !environment.giphyApiKey || environment.giphyApiKey === 'YOUR_GIPHY_API_KEY') {
            return;
        }

        this.loadingGifs = true;
        try {
            const url = `https://api.giphy.com/v1/gifs/search?api_key=${environment.giphyApiKey}&q=${encodeURIComponent(query)}&limit=25&offset=${this.gifOffset}`;
            const response: any = await this.http.get(url).toPromise();
            
            if (response && response.data) {
                this.gifs = [...this.gifs, ...response.data];
                this.hasMoreGifs = response.pagination && response.pagination.count === 25;
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to search GIFs');
        } finally {
            this.loadingGifs = false;
        }
    }

    loadMoreGifs(): void {
        if (!this.loadingGifs && this.hasMoreGifs) {
            this.gifOffset += 25;
            if (this.gifSearchQuery.trim()) {
                this.searchGifs(this.gifSearchQuery);
            } else {
                this.loadTrendingGifs();
            }
        }
    }

    normalizeGiphyUrl(url: string): string {
        if (!url) return url;
        
        try {
            const urlObj = new URL(url);
            
            // If it's a Giphy URL, normalize to i.giphy.com format
            if (urlObj.hostname.includes('giphy.com') && urlObj.pathname.includes('/media/')) {
                // Handle different Giphy URL formats:
                // 1. /media/GIF_ID/size.gif (direct format)
                // 2. /media/v1.Y2lk.../GIF_ID/size.gif (API format with auth token)
                
                // Extract GIF ID and filename from path
                // Pattern: /media/[optional-v1-token/]GIF_ID/filename.gif
                const pathMatch = urlObj.pathname.match(/\/media\/(?:v1\.[^\/]+\/)?([^\/]+)\/([^\/]+\.gif)$/);
                
                if (pathMatch) {
                    const gifId = pathMatch[1]; // The actual GIF ID (e.g., "i8HlBNLnQeFhe")
                    const filename = pathMatch[2]; // The filename (e.g., "200.gif")
                    
                    // Construct direct image URL: https://i.giphy.com/media/GIF_ID/filename.gif
                    return `https://i.giphy.com/media/${gifId}/${filename}`;
                }
                
                // Fallback: if regex doesn't match, try to extract from any media path
                // This handles edge cases where the path structure might be different
                const mediaMatch = urlObj.pathname.match(/\/media\/([^\/]+)\/([^\/]+\.gif)/);
                if (mediaMatch) {
                    // Check if first group looks like a v1 token (long base64-like string)
                    const firstPart = mediaMatch[1];
                    const secondPart = mediaMatch[2];
                    
                    // If first part is a v1 token (contains dots and is long), use second part as GIF ID
                    if (firstPart.includes('v1.') || firstPart.length > 50) {
                        // The actual GIF ID might be in a later segment
                        // Try to find a segment that looks like a GIF ID (short alphanumeric)
                        const segments = urlObj.pathname.split('/').filter(s => s);
                        const mediaIndex = segments.indexOf('media');
                        if (mediaIndex >= 0 && segments.length > mediaIndex + 2) {
                            // Skip the v1 token, get the next segment as GIF ID
                            const potentialGifId = segments[mediaIndex + 2];
                            const potentialFilename = segments[mediaIndex + 3];
                            if (potentialGifId && potentialFilename && potentialFilename.endsWith('.gif')) {
                                return `https://i.giphy.com/media/${potentialGifId}/${potentialFilename}`;
                            }
                        }
                    } else {
                        // First part is likely the GIF ID
                        return `https://i.giphy.com/media/${firstPart}/${secondPart}`;
                    }
                }
            }
        } catch (e) {
            // If URL parsing fails, try simple string replacement as fallback
            if (url.includes('media') && url.includes('giphy.com')) {
                // Replace any media*.giphy.com with i.giphy.com
                url = url.replace(/media\d*\.giphy\.com/g, 'i.giphy.com');
            }
        }
        
        return url;
    }

    async onGifSelect(gif: any): Promise<void> {
        // Extract and normalize GIF URL
        let gifUrl = gif.images?.fixed_height?.url || gif.images?.original?.url || '';
        if (!gifUrl) {
            return;
        }

        // Normalize URL to ensure direct image serving
        gifUrl = this.normalizeGiphyUrl(gifUrl);
        
        // Generate a name for the GIF (use Giphy ID or default)
        const gifName = gif.title || `GIF-${Date.now()}`;
        
        // Close picker immediately for better UX
        this.showGifPicker = false;

        // Ensure we have a valid source (profile/chat/chatRoom)
        if (!this.source) {
            this.notifications.error('Error', 'No conversation selected');
            return;
        }

        try {
            // Upload GIF as attachment
            const uploadObservable = await this.resolutionCenterService.uploadGifAttachment(gifUrl, gifName, 0);
            uploadObservable.subscribe(
                async res => {
                    if (res.response) {
                        if (res.response && res.response.data) {
                            const gifAttachment = res.response.data;
                            const attachmentIds = [gifAttachment.id];
                            const isReply = !!this.replyingToMessage;

                            try {
                                let sendResponse: any;

                                if (isReply && this.replyingToMessage) {
                                    const replyPayload: ReplyMessageRequest = {
                                        message: '',
                                        attachment_ids: attachmentIds
                                    };

                                    // Use correct reply endpoint based on context
                                    if (this.source === 'chatRoom') {
                                        sendResponse = await this.resolutionCenterService.replyToMessageInChatRoom(
                                            this.replyingToMessage.id,
                                            replyPayload
                                        );
                                    } else {
                                        sendResponse = await this.resolutionCenterService.replyToMessage(
                                            this.replyingToMessage.id,
                                            replyPayload
                                        );
                                    }

                                    if (!sendResponse.error) {
                                        this.cancelReply();
                                        this.notifications.success('Success', 'GIF sent');
                                        if (sendResponse.data) {
                                            this.upsertParentMessage(sendResponse.data);
                                        }
                                    } else {
                                        this.notifications.error('Error', 'Failed to send GIF');
                                    }
                                } else {
                                    // Send a new GIF-only message depending on context
                                    switch (this.source) {
                                        case 'profile':
                                        case 'chat': {
                                            if (!this.user?.id) {
                                                this.notifications.error('Error', 'User ID is required');
                                                return;
                                            }

                                            if (this.chat?.id) {
                                                const chatMessage: MessageRequest = {
                                                    message: '',
                                                    attachment_ids: attachmentIds
                                                };
                                                sendResponse = await this.resolutionCenterService.sendMessageToChat(
                                                    this.chat.id,
                                                    chatMessage
                                                );
                                            } else {
                                                const chatMessage: MessageRequest = {
                                                    user_id: this.user.id,
                                                    message: '',
                                                    attachment_ids: attachmentIds
                                                };
                                                sendResponse = await this.resolutionCenterService.sendMessageToContact(
                                                    chatMessage
                                                );
                                            }
                                            break;
                                        }
                                        case 'chatRoom': {
                                            if (!this.chatRoom?.id) {
                                                this.notifications.error('Error', 'Chat room is required');
                                                return;
                                            }

                                            const chatRoomMessage: MessageRequest = {
                                                message: '',
                                                attachment_ids: attachmentIds
                                            };
                                            sendResponse = await this.resolutionCenterService.sendMessageToChatRoom(
                                                this.chatRoom.id,
                                                chatRoomMessage
                                            );
                                            break;
                                        }
                                        default:
                                            this.notifications.error('Error', 'No conversation selected');
                                            return;
                                    }

                                    if (!sendResponse.error && sendResponse.data) {
                                        this.cancelReply(); // Clear reply state after sending
                                        this.notifications.success('Success', 'GIF sent');

                                        // Use the returned messages directly instead of making another API call
                                        const returnedMessages = sendResponse.data.data || [];
                                        if (returnedMessages.length > 0) {
                                            // Update messages with the returned data
                                            this.messages = returnedMessages.sort((a, b) => a.id - b.id);
                                            this.messagesPage = 1;
                                            this.showLoadMoreMessages = sendResponse.data.current_page < sendResponse.data.last_page;
                                            // Chat room: update sidebar list with new last message
                                            if (this.source === 'chatRoom' && this.chatRoom && this.messages.length > 0) {
                                                const latestMessage = this.messages[this.messages.length - 1];
                                                const updatedRoom = { ...this.chatRoom, last_message: latestMessage };
                                                this.chatRoom = updatedRoom;
                                                this.chatRoomUpdated.emit(updatedRoom);
                                            }
                                        } else {
                                            // Fallback: if no messages returned, reload them
                                            this.messagesPage = 1;
                                            await this.loadMessages();
                                        }
                                    } else {
                                        this.notifications.error('Error', 'Failed to send GIF');
                                    }
                                }

                                this.messageSent.emit(true);
                            } catch (error) {
                                this.notifications.error('Error', 'Failed to send GIF');
                            }
                        }
                    } else if (res.error) {
                        // Ignore progress events and other non-error events
                    }
                },
                error => {
                    const err = error.status === 502 ? error.statusText : (error.error?.errorMessages?.join('\r\n') || 'Failed to upload GIF');
                    this.notifications.error('Error', err);
                }
            );
        } catch (error) {
            this.notifications.error('Error', 'Failed to upload GIF');
        }
    }

    closeGifPicker(): void {
        this.showGifPicker = false;
    }

    toggleReactionPicker(messageId: number, event?: MouseEvent): void {
        if (this.showReactionPicker === messageId) {
            this.showReactionPicker = null;
        } else {
            this.showReactionPicker = messageId;
            this.showAttachmentReactionPicker = null;
            // Position the popup after it's rendered
            setTimeout(() => {
                this.positionReactionPopup(messageId, event);
            }, 0);
        }
    }

    positionReactionPopup(messageId: number, event?: MouseEvent): void {
        const popup = document.querySelector(`[data-reaction-popup="${messageId}"]`) as HTMLElement;
        if (!popup) return;

        const button = event?.target as HTMLElement;
        const buttonElement = button?.closest('.btn-icon-sm') as HTMLElement;
        if (!buttonElement) return;

        const buttonRect = buttonElement.getBoundingClientRect();
        const popupWidth = 200; // popup width
        const popupHeight = popup.offsetHeight || 100; // fallback height
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 16; // padding from viewport edges

        // Calculate horizontal position (center on button)
        let left = buttonRect.left + (buttonRect.width / 2) - (popupWidth / 2);
        
        // Adjust if popup goes off left edge
        if (left < padding) {
            left = padding;
        }
        // Adjust if popup goes off right edge
        else if (left + popupWidth > viewportWidth - padding) {
            left = viewportWidth - popupWidth - padding;
        }

        // Calculate vertical position (above the button)
        const top = buttonRect.top - popupHeight - 8; // 8px gap

        // If popup would go off top, position it below instead
        if (top < padding) {
            popup.style.top = `${buttonRect.bottom + 8}px`;
            popup.style.bottom = 'auto';
        } else {
            popup.style.top = `${top}px`;
            popup.style.bottom = 'auto';
        }

        // Set horizontal position
        popup.style.left = `${left}px`;
        popup.style.right = 'auto';
        popup.style.transform = 'none';
        popup.style.position = 'fixed';
    }

    toggleAttachmentReactionPicker(attachmentId: number, event?: MouseEvent): void {
        if (this.showAttachmentReactionPicker === attachmentId) {
            this.showAttachmentReactionPicker = null;
        } else {
            this.showAttachmentReactionPicker = attachmentId;
            this.showReactionPicker = null;
            setTimeout(() => {
                this.positionAttachmentReactionPopup(attachmentId, event);
            }, 0);
        }
    }

    positionAttachmentReactionPopup(attachmentId: number, event?: MouseEvent): void {
        const popup = document.querySelector(`[data-attachment-reaction-popup="${attachmentId}"]`) as HTMLElement;
        if (!popup) return;

        const button = event?.target as HTMLElement;
        const buttonElement = button?.closest('.btn-icon-sm') as HTMLElement;
        if (!buttonElement) return;

        const buttonRect = buttonElement.getBoundingClientRect();
        const popupWidth = 200;
        const popupHeight = popup.offsetHeight || 100;
        const viewportWidth = window.innerWidth;
        const padding = 16;

        let left = buttonRect.left + (buttonRect.width / 2) - (popupWidth / 2);

        if (left < padding) {
            left = padding;
        } else if (left + popupWidth > viewportWidth - padding) {
            left = viewportWidth - popupWidth - padding;
        }

        const top = buttonRect.top - popupHeight - 8;

        if (top < padding) {
            popup.style.top = `${buttonRect.bottom + 8}px`;
            popup.style.bottom = 'auto';
        } else {
            popup.style.top = `${top}px`;
            popup.style.bottom = 'auto';
        }

        popup.style.left = `${left}px`;
        popup.style.right = 'auto';
        popup.style.transform = 'none';
        popup.style.position = 'fixed';
    }

    private async send(attachmentIds: number[]): Promise<any> { //todo: fix this type
        let response: any;

        switch (this.source) {
            case 'profile':
                // For profile context: if we have a chat, use sendMessageToChat, otherwise use sendMessageToContact
                if (!this.user?.id) return;

                if (this.chat?.id) {
                    // User has an existing chat
                    const chatMessage: MessageRequest = {
                        message: this.messageText,
                        attachment_ids: attachmentIds,
                        scheduled_at: this.scheduledDateTime?.toISOString(),
                    };
                    
                    response = await this.resolutionCenterService.sendMessageToChat(this.chat.id, chatMessage);
                } else {
                    // User doesn't have a chat yet - start a new chat
                    const chatMessage: MessageRequest = {
                        user_id: this.user.id,
                        message: this.messageText,
                        attachment_ids: attachmentIds,
                        scheduled_at: this.scheduledDateTime?.toISOString(),
                    };
                    
                    response = await this.resolutionCenterService.sendMessageToContact(chatMessage);
                }

                break;
            case 'chat':
                // For chat context: if we have a chat, use sendMessageToChat, otherwise use sendMessageToContact
                if (!this.user?.id) return;

                if (this.chat?.id) {
                    // User has an existing chat
                    const chatMessage: MessageRequest = {
                        message: this.messageText,
                        attachment_ids: attachmentIds,
                        scheduled_at: this.scheduledDateTime?.toISOString(),
                    };
                    
                    response = await this.resolutionCenterService.sendMessageToChat(this.chat.id, chatMessage);
                } else {
                    // User doesn't have a chat yet - start a new chat
                    const chatMessage: MessageRequest = {
                        user_id: this.user.id,
                        message: this.messageText,
                        attachment_ids: attachmentIds,
                        scheduled_at: this.scheduledDateTime?.toISOString(),
                    };
                    
                    response = await this.resolutionCenterService.sendMessageToContact(chatMessage);
                }

                break;
            case 'chatRoom':
                if (!this.chatRoom?.id) return;
                
                const chatRoomMessage: MessageRequest = {
                    message: this.messageText,
                    attachment_ids: attachmentIds,
                    scheduled_at: this.scheduledDateTime?.toISOString(),
                };
                
                response = await this.resolutionCenterService.sendMessageToChatRoom(this.chatRoom.id, chatRoomMessage);
                break;
            case 'broadcast': {
                if (!this.broadcastAudience?.audience_key) return;

                const broadcastId = this.broadcastEditingMessage?.id ?? 0;
                const scope = this.broadcastAudience.scope ?? [];
                const broadcastBody: BroadcastUpsertRequest = {
                    message: this.messageText?.trim() ?? '',
                    attachment_ids: attachmentIds.length ? attachmentIds : undefined,
                    scheduled_at: this.scheduledDateTime?.toISOString(),
                    scope,
                };

                response = await this.resolutionCenterService.upsertBroadcastMessage(broadcastId, broadcastBody);
                break;
            }
            default:
                this.sending = false;
                return;
        }

        if (!response?.error) {
            this.messageSent.emit(true);
        }

        return response;
    }

    private async reply(attachmentIds: number[]): Promise<any> { //todo: fix this type
        let response: any | undefined;
        const parentId = this.replyingToMessage?.id;

        if (!parentId) return response;

        const replyMessage: ReplyMessageRequest = {
            message: this.messageText,
            attachment_ids: attachmentIds
        };

        switch (this.source) {
            case 'profile':
            case 'chat':
                response = await this.resolutionCenterService.replyToMessage(parentId, replyMessage);
                break;
            case 'chatRoom':
                response = await this.resolutionCenterService.replyToMessageInChatRoom(parentId, replyMessage);
                break;
            case 'broadcast':
                break;
        }

        return response;
    }

    private replaceAttachmentInMessages(updatedAttachment: Attachment): void {
        const updateAttachmentArray = (attachments?: Attachment[]): boolean => {
            if (!attachments || attachments.length === 0) return false;
            const index = attachments.findIndex(a => a.chat_message_attachment_id === updatedAttachment.chat_message_attachment_id);
            if (index === -1) return false;
            attachments[index] = { ...updatedAttachment };
            return true;
        };

        let changed = false;

        for (const message of this.messages) {
            if (updateAttachmentArray(message.attachments)) {
                message.attachments = [...message.attachments];
                changed = true;
            }

            if (message.thread && message.thread.length > 0) {
                for (const threadMessage of message.thread) {
                    if (updateAttachmentArray(threadMessage.attachments)) {
                        threadMessage.attachments = [...threadMessage.attachments];
                        changed = true;
                    }
                }
            }
        }

        if (changed) {
            this.messages = [...this.messages];
        }
    }

    private getChatApplicationId(): number | undefined {
        const chat: any = this.chat;
        return chat?.application_id ?? chat?.applicationId;
    }

    private updateChatFlags(updatedChat?: any): void {
        if (!this.chat) return;
        const target: any = this.chat;
        const updates = updatedChat || {};

        // Normalize favorite keys
        const normalizedUpdates = {
            ...updates,
            is_favorited: updates.is_favorited ?? target.is_favorited,
            is_pinned: updates.is_pinned ?? target.is_pinned,
            is_archived: updates.is_archived ?? target.is_archived
        };

        if (this.refreshListOnFlagsChange) {
            this.chatUpdated.emit({ ...(this.chat as any), ...normalizedUpdates } as ClientChat);
            return;
        }

        Object.assign(target, normalizedUpdates);
        // Reassign to trigger change detection in case OnPush is enabled elsewhere
        this.chat = { ...(this.chat as any) };
        this.chatUpdated.emit(this.chat as ClientChat);
    }

    private updateChatRoomFlags(updatedChatRoom?: any): void {
        if (!this.chatRoom) return;
        const target: any = this.chatRoom;
        const updates = updatedChatRoom || {};

        // Normalize favorite keys
        const normalizedUpdates = {
            ...updates,
            is_favorited: updates.is_favorited ?? target.is_favorited,
            is_pinned: updates.is_pinned ?? target.is_pinned,
            is_archived: updates.is_archived ?? target.is_archived
        };

        if (this.refreshListOnFlagsChange) {
            this.chatRoomUpdated.emit({ ...(this.chatRoom as any), ...normalizedUpdates } as ChatRoom);
            return;
        }

        Object.assign(target, normalizedUpdates);
        // Reassign to trigger change detection in case OnPush is enabled elsewhere
        this.chatRoom = { ...(this.chatRoom as any) };
        this.chatRoomUpdated.emit(this.chatRoom as ChatRoom);
    }

    toggleCustomGroupChatMenu(event?: MouseEvent): void {
        if (event) {
            event.stopPropagation();
        }
        this.showGroupChatMenu = !this.showGroupChatMenu;
    }

    onEditCustomGroupChat(): void {
        this.showGroupChatMenu = false;
        
        if (!this.chatRoom) return;

        // Emit event to parent to open edit modal for custom chat room
        this.editCustomChatRoom.emit(this.chatRoom);
    }

    onManageCustomGroupChatMembers(): void {
        this.showGroupChatMenu = false;
        this.showManageMembersModal = true;
    }

    onAddNewMembersFromManageModal(): void {
        this.showManageMembersModal = false;
        if (!this.chatRoom) return;
        this.addMembersToChatRoom.emit(this.chatRoom);
    }

    onCloseManageMembersModal(): void {
        this.showManageMembersModal = false;
    }

    async onDeleteCustomGroupChat(): Promise<void> {
        this.showGroupChatMenu = false;
        
        if (!this.chatRoom?.id) return;

        const confirmed = await this.interaction.confirm({
            title: 'Delete group chat',
            message: 'Are you sure you want to delete this group chat? This action can\'t be undone and all messages in the group will be permanently removed.',
            buttons: [
                {
                    label: 'Keep group chat',
                    value: ConfirmStatus.CANCELED,
                    style: 'default'
                },
                {
                    label: 'Delete group chat',
                    value: ConfirmStatus.CONFIRMED,
                    style: 'danger'
                }
            ]
        });

        if (!confirmed) return;

        try {
            const deletedChatRoomId = this.chatRoom.id;
            const response = await this.resolutionCenterService.deleteChatRoom(deletedChatRoomId);
            if (!response.error) {
                this.notifications.success('Success', 'Group chat deleted successfully');
                // Reset component state after deletion
                this.resetComponentState();
                // Emit null to signal deletion - parent will remove it from the list
                this.chatRoomUpdated.emit(null);
            } else {
                this.notifications.error('Error', 'Failed to delete group chat');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to delete group chat');
        }
    }

    onEditSystemChatRoom(): void {
        if (!this.chatRoom) return;

        // Emit event to parent to open edit modal for system chat room
        this.editSystemChatRoom.emit(this.chatRoom);
    }

    private resetComponentState(): void {
        // Reset all component state when inputs are cleared
        this.stopNewMessagesPoll();
        this.user = undefined;
        this.chat = undefined;
        this.chatRoom = undefined;
        this.internalUser = undefined; // Clear internal user tracking
        this.messages = [];
        this.messageText = '';
        this.messagesPage = 1;
        this.showLoadMoreMessages = false;
        this.replyingToMessage = null;
        this.replyingToAttachment = null;
        this.resetAttachmentState();
        this.selectedUserForNewChat = null;
        this.userSearchQuery = '';
        this.userSearchResults = [];
        this.previousUserId = null;
        this.previousChatId = null; // Reset chat ID tracking
        this.hasAttemptedLoad = false; // Reset load attempt flag when component is reset
        this.previousChatRoomId = null; // Reset chat room ID tracking
        this.isNewChat = true;
        this.showGroupChatMenu = false;
        this.scheduledDateTime = null;
        this.scheduledMessages = [];
        this.scheduledMessageBeingEdited = null;
    }

    private resetAttachmentState(): void {
        this.uploadedFiles = [];
        this.fileList = [];
        this.totalUploads = 0;
        this._uploadedCounter = 0;
        this.currentUploadIndex = 0;
        this.uploadProgress = 0;
        this.currentAttachmentType = null;
    }

    openScheduleModal(messageToReschedule?: ClientMessage | ChatRoomMessage | BroadcastMessage): void {
        // Prepare initial state for the modal (used both for new schedule and reschedule)
        let initialState: any = {};

        this.scheduledMessageToReschedule = messageToReschedule || null;

        if (messageToReschedule && (messageToReschedule.scheduled_at || messageToReschedule.created_at)) {
            const baseDate = new Date(messageToReschedule.scheduled_at || messageToReschedule.created_at);
            const hours = baseDate.getHours().toString().padStart(2, '0');
            const minutes = baseDate.getMinutes().toString().padStart(2, '0');

            initialState = {
                selectedDate: baseDate,
                selectedTime: `${hours}:${minutes}`
            };
        }

        this.bsModalRef = this.modalService.show(ScheduleMessageModalComponent, {
            initialState,
            backdrop: true,
            ignoreBackdropClick: false,
            class: 'schedule-message-modal-container'
        });

        if (this.bsModalRef.content) {
            this.bsModalRef.content.onClose.subscribe((scheduledDate: Date | null) => {
                if (scheduledDate) {
                    this.scheduledDateTime = scheduledDate;

                    if (this.scheduledMessageToReschedule) {
                        // Reschedule existing scheduled message
                        this.rescheduleScheduledMessage(this.scheduledMessageToReschedule, scheduledDate);
                    } else {
                        // Initial scheduling – trigger sending the message with scheduled_at set
                        this.sendMessage();
                    }
                }

                // Clear the reschedule target after modal closes
                this.scheduledMessageToReschedule = null;
                // Also exit edit mode if we were editing (defensive)
                this.scheduledMessageBeingEdited = null;
            });
        }
    }

    private formatScheduledDate(date: Date): string {
        const options: Intl.DateTimeFormatOptions = {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        return date.toLocaleString('en-US', options);
    }

    private async rescheduleScheduledMessage(
        message: ClientMessage | ChatRoomMessage | BroadcastMessage,
        scheduledDate: Date,
    ): Promise<void> {
        if (!this.source) return;

        const scheduledAtIso = scheduledDate.toISOString();
        const attachmentIds = this.getScheduledMessageAttachmentIds(message);

        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat': {
                    const payload: MessageRequest = {
                        message_id: message.id,
                        message: message.message || '',
                        attachment_ids: attachmentIds,
                        scheduled_at: scheduledAtIso,
                    };
                    if (!this.chat?.id) return;
                    response = await this.resolutionCenterService.sendMessageToChat(this.chat.id, payload);
                    break;
                }
                case 'chatRoom': {
                    const payload: MessageRequest = {
                        message_id: message.id,
                        message: message.message || '',
                        attachment_ids: attachmentIds,
                        scheduled_at: scheduledAtIso,
                    };
                    if (!this.chatRoom?.id) return;
                    response = await this.resolutionCenterService.sendMessageToChatRoom(this.chatRoom.id, payload);
                    break;
                }
                case 'broadcast': {
                    const payload: BroadcastUpsertRequest = {
                        message: message.message || '',
                        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
                        scheduled_at: scheduledAtIso,
                        scope: this.getBroadcastScope(),
                    };
                    response = await this.resolutionCenterService.upsertBroadcastMessage(message.id, payload);
                    break;
                }
                default:
                    return;
            }

            if (!response?.error) {
                this.notifications.success('Message rescheduled', 'The scheduled time has been updated.');
                await this.loadScheduledMessages();
            } else {
                this.notifications.error('Error', 'Failed to reschedule message');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to reschedule message');
        }
    }

    async confirmScheduledMessageEdit(): Promise<void> {
        if (!this.scheduledMessageBeingEdited) return;

        const target = this.scheduledMessageBeingEdited;
        const updatedText = (this.messageText || '').trim();

        if (!updatedText) {
            this.notifications.warning('Message content required', 'Please enter a message before confirming.');
            return;
        }

        const attachmentIds = this.getScheduledMessageAttachmentIds(target);

        try {
            let response: any;

            switch (this.source) {
                case 'profile':
                case 'chat': {
                    const payload: MessageRequest = {
                        message_id: target.id,
                        message: updatedText,
                        attachment_ids: attachmentIds,
                        scheduled_at: target.scheduled_at,
                    };
                    if (!this.chat?.id) return;
                    response = await this.resolutionCenterService.sendMessageToChat(this.chat.id, payload);
                    break;
                }
                case 'chatRoom': {
                    const payload: MessageRequest = {
                        message_id: target.id,
                        message: updatedText,
                        attachment_ids: attachmentIds,
                        scheduled_at: target.scheduled_at,
                    };
                    if (!this.chatRoom?.id) return;
                    response = await this.resolutionCenterService.sendMessageToChatRoom(this.chatRoom.id, payload);
                    break;
                }
                case 'broadcast': {
                    const payload: BroadcastUpsertRequest = {
                        message: updatedText,
                        attachment_ids: attachmentIds.length ? attachmentIds : undefined,
                        scheduled_at: target.scheduled_at,
                        scope: this.getBroadcastScope(),
                    };
                    response = await this.resolutionCenterService.upsertBroadcastMessage(target.id, payload);
                    break;
                }
                default:
                    return;
            }

            if (!response?.error) {
                this.notifications.success('Message updated', 'Scheduled message content has been updated.');
                this.scheduledMessageBeingEdited = null;
                this.messageText = '';
                await this.loadScheduledMessages();
            } else {
                this.notifications.error('Error', 'Failed to update scheduled message');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to update scheduled message');
        }
    }

    // Placeholder actions for scheduled messages list
    onViewAllScheduledMessages(): void {
        if (!this.scheduledMessages || this.scheduledMessages.length === 0) {
            return;
        }

        const initialState = {
            scheduledMessages: [...this.scheduledMessages],
            onEdit: (message: ClientMessage | ChatRoomMessage | BroadcastMessage) => this.onEditScheduledMessage(message),
            onReschedule: (message: ClientMessage | ChatRoomMessage | BroadcastMessage) => this.onRescheduleScheduledMessage(message),
            onDelete: (message: ClientMessage | ChatRoomMessage | BroadcastMessage) => this.onDeleteScheduledMessage(message),
        };

        this.bsModalRef = this.modalService.show(ScheduledMessagesModalComponent, {
            initialState,
            backdrop: true,
            ignoreBackdropClick: false,
            class: 'scheduled-messages-list-modal'
        });
    }

    onEditScheduledMessage(message: ClientMessage | ChatRoomMessage | BroadcastMessage): void {
        this.scheduledMessageBeingEdited = message;
        this.messageText = message.message || '';
        if (this.source !== 'broadcast') {
            this.scrollToBottom();
        }
        setTimeout(() => this.adjustMessageInputHeight(), 0);
    }

    onRescheduleScheduledMessage(message: ClientMessage | ChatRoomMessage | BroadcastMessage): void {
        this.openScheduleModal(message);
    }

    async onDeleteScheduledMessage(message: ClientMessage | ChatRoomMessage | BroadcastMessage): Promise<void> {
        if (this.source === 'broadcast') {
            await this.deleteScheduledBroadcast(message as BroadcastMessage);
            return;
        }
        await this.deleteMessage(message as ClientMessage);
        await this.loadScheduledMessages();
    }

    private getBroadcastScope() {
        return this.broadcastAudience?.scope ?? [];
    }

    private getScheduledMessageAttachmentIds(
        message: ClientMessage | ChatRoomMessage | BroadcastMessage,
    ): number[] {
        return (message.attachments || []).map((a) => a.attachment.id);
    }

    private async deleteScheduledBroadcast(message: BroadcastMessage): Promise<void> {
        const confirmed = await this.interaction.confirm({
            title: 'Delete scheduled broadcast?',
            message: 'Are you sure you want to delete this scheduled broadcast? This action is permanent and cannot be undone.',
            buttons: [
                {
                    label: 'Cancel',
                    value: ConfirmStatus.CANCELED,
                    style: 'default',
                },
                {
                    label: 'Delete broadcast',
                    value: ConfirmStatus.CONFIRMED,
                    style: 'danger',
                },
            ],
        });

        if (!confirmed) {
            return;
        }

        try {
            const response = await this.resolutionCenterService.deleteBroadcastMessage(message.id);
            if (!response?.error) {
                this.notifications.success('Success', 'Scheduled broadcast deleted');
                this.broadcastMessageRemoved.emit(message.id);
                await this.loadScheduledMessages();
            } else {
                this.notifications.error('Error', 'Failed to delete scheduled broadcast');
            }
        } catch (error) {
            this.notifications.error('Error', 'Failed to delete scheduled broadcast');
        }
    }
}
